import type { SeasonDefinition } from '../types';

// October 2026. Moonlit midnight canvas, ash-ivory text, tarnished-silver
// hairlines and one restrained pumpkin accent. Los Angeles midnight on both
// ends. It runs through the Knock, Knock claim week, because its entry link is
// the way to the event page while badges can still be claimed (PST by then).
export const HALLOWEEN_2026: SeasonDefinition = {
  id: 'halloween-2026',
  look: 'halloween',
  label: 'Halloween',
  window: {
    start: '2026-10-01T00:00:00-07:00',
    end: '2026-11-08T00:00:00-08:00',
  },
  tokens: {
    '--color-background': '#07080c',
    '--color-background-tertiary': '#0f1016',
    '--color-glass-ink': '#08090d',

    '--color-textPrimary': '#f5f1ea',
    // 4.5:1 or better on what they sit on (Brandon, 2026-10-17). The footer's
    // muted words sit over the mist art (L 0.042 at worst), which needs muted
    // at L 0.364 or more; secondary rises with it so it stays a step above.
    // Were #a3a09a / #73706a (muted measured 2.31:1 over the mist).
    '--color-textSecondary': '#c6c2ba',
    '--color-textMuted': '#aaa69f',

    '--color-accent': '#e3954f',
    '--color-accent-hover': '#efb27e',
    '--color-accent-muted': 'rgba(227, 149, 79, 0.5)',
    '--color-accent-dim': 'rgba(227, 149, 79, 0.12)',

    '--color-border': 'rgba(222, 218, 208, 0.1)',
    '--color-borderLight': 'rgba(222, 218, 208, 0.06)',
    '--color-borderSubtle': 'rgba(222, 218, 208, 0.04)',
  },
  themeColor: '#07080c',
  entry: {
    to: '/events/knock-knock',
    label: 'Knock, knock',
    title: 'The Halloween event',
  },
  // The event page draws its own moon and fog.
  quietRoutes: ['/events/knock-knock'],
  // Raster art: drop a file with this name into public/seasonal/halloween/
  // and it replaces that piece's fallback. Sizes, alpha and tiling for each
  // piece are in the art request (REQUESTS.md); a missing file costs nothing.
  art: {
    'hero-scene': '/seasonal/halloween/hero-scene.webp',
    'hero-fog': '/seasonal/halloween/hero-fog.webp',
    moon: '/seasonal/halloween/moon.webp',
    mist: '/seasonal/halloween/mist.webp',
    ember: '/seasonal/halloween/ember.webp',
    ironwork: '/seasonal/halloween/ironwork.webp',
    cobweb: '/seasonal/halloween/cobweb.webp',
    logo: '/seasonal/halloween/logo.webp',
    'not-found': '/seasonal/halloween/not-found.webp',
    favicon: '/seasonal/halloween/favicon.png',
    // The event's door knocker (plate + ring, native-resolution crops of the event art)
    // as the header entry's emblem; the ring taps on hover.
    // Web-size crops (scripts/export-event-art.mjs); EntryMark adds the 1x/3x.
    'entry-mark': '/seasonal/halloween/web/entry-knocker-plate-44.webp',
    'entry-mark-part': '/seasonal/halloween/web/entry-knocker-ring-44.webp',
  },
  fonts: ['/seasonal/fonts/PirataOne-Regular.woff2'],
  // Blackletter only where it stays legible: short titles. Member names (the
  // nowrap h1 on /u/) keep the house face, a name is identity.
  display: {
    selector: 'main h1:not(.whitespace-nowrap), main h2:is(.text-2xl, .text-3xl, .text-4xl, .text-5xl, .font-serif), main .not-found-code',
    maxChars: 30,
  },
  ogImage: [
    '/seasonal/halloween/og-image.png',
    '/seasonal/halloween/og-image.jpg',
    '/seasonal/halloween/og-image.webp',
  ],
  copy: {
    'hero.title.lead': 'StreamNook,',
    // Non-breaking space: the candle half never splits across lines.
    'hero.title.em': 'after\u00a0dark.',
    'notFound.title': 'Nobody home.',
    'notFound.body': 'You knocked, but nothing lives at this address.',
    'notFound.action': 'Back to the light',
  },
};
